import { Text } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import dayjs from 'dayjs'

import classes from '../LogVolunteeringActivity.module.scss'
import { LogVolunteeringActivityForm } from '../LogVolunteeringActivityForm/LogVolunteeringActivityForm'
import { LogVolunteeringActivityCancellationModal } from '../Modals/LogVolunteeringActivityCancellationModal'
import { LogVolunteeringActivitySuccessModal } from '../Modals/LogVolunteeringActivitySuccessModal'
import { useVolunteeringActivityValidationSchema } from '../useVolunteeringActivityValidationSchema'
import { formatTimeLogsForSubmit, buildSubmitPayload } from '../volunteeringActivitySubmitHelpers'
import type { LogVolunteeringFormType, LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'

import { useModal, TimePickerHelpers } from '@percent/domain/giving'
import { FullPageFormLayout, Loader } from '@percent/lemonade'
import { useAuth, useLogger, useMutation, useQuery } from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { getVolunteeringActivity } from '@percent/workplace-giving/api/volunteering/getVolunteeringActivity/getVolunteeringActivity'
import { okResponse } from '@percent/workplace-giving/api/goodstack'
import { editVolunteeringActivity } from '@percent/workplace-giving/api/volunteering/editVolunteeringActivity/editVolunteeringActivity'
import { TIME_LOG_OVERLAP_CODE } from '@percent/workplace-giving/api/volunteering/logVolunteeringActivity/logVolunteeringActivity'
import { WizardHeader } from '@percent/workplace-giving/common/components/WizardHeader/WizardHeader'
import { UnexpectedErrorModal } from '@percent/workplace-giving/common/components/UnexpectedErrorModal/UnexpectedErrorModal'
import {
  getAccountFromAuthState,
  getCountryCodeFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { VolunteeringTimeLogType } from '@percent/workplace-giving/api/opportunity/dto'
import { getOpportunityDetails } from '@percent/workplace-giving/api/opportunity/getOpportunityDetails/getOpportunityDetails'

export function EditVolunteeringActivity() {
  const { track } = useAnalytics()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { timeLogId } = useParams()
  const { logError } = useLogger()
  const { state: authState } = useAuth()
  const userAcc = getAccountFromAuthState(authState)!
  const defaultCountry = getCountryCodeFromAuthState(authState)!

  const { requireOrgFieldsInForms, searchVerifiedOrgs, volunteeringWorkingHoursCheck } =
    getPartnerFromAuthState(authState)!

  const getValidationSchema = useVolunteeringActivityValidationSchema()

  const { isOpen: isErrorModalOpen, openModal: openErrorModal, closeModal: closeErrorModal } = useModal()
  const {
    isOpen: isCancellationModalOpen,
    openModal: openCancellationModal,
    closeModal: closeCancellationModal
  } = useModal()
  const { isOpen: isSuccessModalOpen, openModal: openSuccessModal, closeModal: closeSuccessModal } = useModal()

  const [isDetailsFetchingErrorOpen, setIsDetailsFetchingErrorOpen] = useState(false)
  const [overlapSnapshot, setOverlapSnapshot] = useState<string | null>(null)

  const [initialFormValues, setInitialFormValues] = useState<LogVolunteeringActivityFormFields>({
    timeLogs: [{ date: undefined, startTime: undefined, endTime: undefined }],
    organisationCountry: defaultCountry || '',
    organisationId: '',
    activities: [],
    platform: VolunteeringTimeLogType.OffPlatform,
    opportunityId: '',
    skills: [],
    duringWorkingHours: undefined
  })

  const navigateBackToMyImpact = () => {
    navigate(AppRoute.MY_IMPACT, { state: { activeTab: 'volunteer' } })
  }

  const {
    data: activityLogData,
    isSuccess,
    isPending,
    isError,
    error: activityLogFetchingError
  } = useQuery(['getVolunteeringActivity', timeLogId as string], getVolunteeringActivity, {
    enabled: !!timeLogId
  })

  const { data: editedLogOpportunity, isPending: isLoadingEditedLogOpportunity } = useQuery(
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    ['getOpportunityDetails', activityLogData?.opportunity?.id!],
    getOpportunityDetails,
    { enabled: !!activityLogData?.opportunity?.id }
  )

  const canEdit = activityLogData?.accountId === userAcc.id

  useEffect(() => {
    if (!isPending && activityLogData && !canEdit) {
      navigate(-1)
    }
  }, [navigate, activityLogData, canEdit, isPending])

  useEffect(() => {
    if (isError && activityLogFetchingError) {
      logError(activityLogFetchingError)
      setIsDetailsFetchingErrorOpen(true)
    }
  }, [activityLogFetchingError, isError, logError])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isSuccess) {
      const is12h = TimePickerHelpers.is12hourFormat()
      const start = dayjs(activityLogData.startTime)
      const end = dayjs(activityLogData.endTime)

      return setInitialFormValues({
        timeLogs: [
          {
            date: start.toDate(),
            startTime: TimePickerHelpers.formatTime(start.format('HH:mm'), is12h),
            endTime: TimePickerHelpers.formatTime(end.format('HH:mm'), is12h)
          }
        ],
        organisationCountry: activityLogData.organisation?.countryCode || '',
        organisationId: activityLogData.organisation?.id,
        activities: activityLogData.activities?.map(({ id }) => id),
        platform: activityLogData.type,
        opportunityId: activityLogData?.opportunity?.id,
        skills: activityLogData?.skills?.map(({ id }) => id) ?? [],
        duringWorkingHours:
          activityLogData?.duringWorkingHours !== undefined ? activityLogData.duringWorkingHours : undefined
      })
    }
  }, [isSuccess, activityLogData, timeLogId])

  const { mutateAsync } = useMutation({
    mutationFn: editVolunteeringActivity,
    onSuccess: res => {
      if (okResponse(res)) {
        setOverlapSnapshot(null)
        track({
          event: 'Volunteering Time Log Edit Completed',
          properties: { timeLogId }
        })

        return openSuccessModal()
      }

      if (res.status === 409 && res.error.code === TIME_LOG_OVERLAP_CODE) {
        setOverlapSnapshot(JSON.stringify(values.timeLogs))

        return undefined
      }

      setOverlapSnapshot(null)

      return openErrorModal()
    },
    onError: error => {
      setOverlapSnapshot(null)
      openErrorModal()
      logError(error)
    }
  })

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: () =>
      getValidationSchema({
        mode: 'edit',
        platform: values.platform,
        requireOrgFieldsInForms,
        volunteeringWorkingHoursCheck
      }),
    onSubmit: data => {
      const { timeLogs, platform, duringWorkingHours } = data as unknown as LogVolunteeringFormType
      const isResubmittingAfterOverlap = overlapSnapshot !== null && overlapSnapshot === JSON.stringify(data.timeLogs)

      const formattedTimeLogs = formatTimeLogsForSubmit(timeLogs)
      const props = {
        ...buildSubmitPayload(platform, formattedTimeLogs, duringWorkingHours, {
          organisationId: data?.organisationId,
          activities: data.activities as string[],
          skills: data.skills as string[],
          opportunityId: data.opportunityId as string
        }),
        timeLogId: timeLogId as string,
        ...(isResubmittingAfterOverlap && { confirmed: true })
      }

      return mutateAsync([props])
    },
    validateOnBlur: true,
    validateOnChange: true
  })

  const {
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    dirty,
    validateForm
  } = formik

  useEffect(() => {
    validateForm()
  }, [validateForm, values.organisationId, values.organisationCountry, values.duringWorkingHours])

  const showOverlapWarning = overlapSnapshot !== null && overlapSnapshot === JSON.stringify(values.timeLogs)

  useEffect(() => {
    if (editedLogOpportunity && editedLogOpportunity.participation?.status === 'participating') {
      setFieldValue('opportunityId', editedLogOpportunity.id)
      setFieldTouched('opportunityId', false, true)
    }
  }, [editedLogOpportunity, setFieldTouched, setFieldValue])

  const formValid = isValid && dirty && !isSubmitting

  if (
    (((timeLogId && isPending) ||
      (timeLogId && !values.timeLogs) ||
      (!!activityLogData?.opportunity?.id && isLoadingEditedLogOpportunity)) &&
      !isError) ||
    (!isError && !canEdit)
  ) {
    return <Loader />
  }

  const preloadedOpportunity = activityLogData?.opportunity ? (activityLogData.opportunity as any) : undefined

  const defaultOrg = activityLogData?.organisation
    ? {
        id: activityLogData.organisation.id,
        name: activityLogData.organisation.name,
        countryCode: activityLogData.organisation.countryCode
      }
    : undefined

  return (
    <FullPageFormLayout
      actionsHeader={
        <WizardHeader
          title={t('workplace_giving.volunteering.activityForm.editYourActivity')}
          onCancel={() => {
            if (dirty) {
              openCancellationModal()
            } else {
              navigateBackToMyImpact()
            }
          }}
          onPrimaryBtn={handleSubmit}
          isPrimaryBtnDisabled={!formValid}
          isPrimaryBtnLoading={isSubmitting}
          primaryBtnLabel={t('workplace_giving.common.saveChanges')}
        />
      }
    >
      <Text className={classes.Title}>{t('workplace_giving.volunteering.activityForm.title')}</Text>
      <Text className={classes.Description}>{t('workplace_giving.volunteering.activityForm.description')}</Text>

      <LogVolunteeringActivityForm
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        formikInstance={formik}
        showPlatformSelector={false}
        allowMultipleEntries={false}
        allowOpportunityChange={false}
        showWorkingHoursCheck={!!volunteeringWorkingHoursCheck}
        workingHoursRequired={false}
        defaultOpportunity={editedLogOpportunity}
        defaultOrg={defaultOrg as any}
        defaultCountryCode={activityLogData?.organisation?.countryCode || defaultCountry}
        requireNonprofitFields={requireOrgFieldsInForms}
        searchOrgsWithEligibilityCheck={searchVerifiedOrgs}
        disableCountryChange
        preloadedOpportunity={preloadedOpportunity}
        showOverlapWarning={showOverlapWarning}
      />

      <LogVolunteeringActivityCancellationModal
        open={isCancellationModalOpen}
        onClose={closeCancellationModal}
        message={t('workplace_giving.volunteering.activityForm.cancelEdit.modal')}
        onConfirm={navigateBackToMyImpact}
      />
      <LogVolunteeringActivitySuccessModal
        open={isSuccessModalOpen}
        onClose={closeSuccessModal}
        onConfirm={() => {
          closeSuccessModal()
          navigateBackToMyImpact()
        }}
        title={t('workplace_giving.volunteering.editActivityForm.success.modal.title')}
        description={t('workplace_giving.volunteering.editActivityForm.success.modal.description')}
      />
      <UnexpectedErrorModal
        message={t('workplace_giving.errors.api.editVolunteeringTime.title')}
        description={t('workplace_giving.errors.api.editVolunteeringTime.description')}
        open={isErrorModalOpen}
        onClose={closeErrorModal}
      />
      <UnexpectedErrorModal
        message={t('workplace_giving.errors.somethingWentWrong')}
        open={isDetailsFetchingErrorOpen}
        onClose={() => {
          setIsDetailsFetchingErrorOpen(false)
          navigateBackToMyImpact()
        }}
      />
    </FullPageFormLayout>
  )
}
